/**
 * Konten bei Meta (ab 0.16.0): Instagram und Facebook-Seite verbinden, dann
 * entscheiden, ob freigegebene Beitraege zu ihrem Termin von selbst erscheinen.
 *
 * Verbinden heisst noch nicht posten: „Automatisch posten“ ist aus, bis der
 * Kunde es einschaltet. Gepostet wird ohnehin nur, was freigegeben ist.
 * Der Login laeuft bei Meta; danach kommt der Browser auf diese Seite zurueck,
 * mit `?meta=verbunden` (oder `abgebrochen`, `fehler`, `seite_waehlen`).
 */

import { useEffect, useState } from "react";

import type { HubSocialKonto } from "../core/types.js";
import { useSocialKonten } from "./hooks.js";
import { fehlerText, KANAL } from "./Planung.js";

export interface SocialKontenProps {
  basePath?: string;
  userName?: string;
}

const RUECKMELDUNG: Record<string, string> = {
  verbunden: "Verbunden. Schalten Sie „Automatisch posten“ ein, wenn freigegebene Beiträge von selbst erscheinen sollen.",
  seite_waehlen: "Fast fertig — bitte wählen Sie die Facebook-Seite, auf die gepostet wird.",
  abgebrochen: "Die Verbindung wurde nicht hergestellt.",
};

function ablauf(iso: string | null): string {
  if (!iso) return "";
  const tage = Math.round((new Date(iso).getTime() - Date.now()) / 86_400_000);
  return tage > 14 ? "" : tage > 0 ? `läuft in ${tage} Tagen ab — wird automatisch verlängert` : "abgelaufen";
}

export function SocialKonten({ basePath, userName = "" }: SocialKontenProps) {
  const k = useSocialKonten(basePath);
  const [meldung, setMeldung] = useState<string | null>(null);

  useEffect(() => {
    // Rueckkehr von Meta: einmal anzeigen, dann aus der Adresse nehmen.
    const q = new URLSearchParams(window.location.search);
    const meta = q.get("meta");
    if (!meta) return;
    setMeldung(meta === "fehler" ? `Die Verbindung hat nicht geklappt: ${q.get("grund") ?? "unbekannter Fehler"}`
      : RUECKMELDUNG[meta] ?? null);
    for (const schluessel of ["meta", "plattform", "grund"]) q.delete(schluessel);
    const rest = q.toString();
    window.history.replaceState(null, "", `${window.location.pathname}${rest ? `?${rest}` : ""}${window.location.hash}`);
  }, []);

  if (k.loading && !k.data) return <p className="ch-muted">Lädt …</p>;
  if (k.error) return <p className="ch-hinweis">Die Konten konnten nicht geladen werden: {k.error}</p>;
  const konten = k.data ?? [];

  return (
    <div className="ch-konten">
      <p className="ch-muted ch-intro">
        Verbundene Konten posten freigegebene Beiträge zu ihrem Termin selbst — nur, wenn Sie es einschalten.
        Ohne Verbindung bleibt es beim Kopieren und Melden mit Link.
      </p>
      {meldung && <p className="ch-hinweis">{meldung}</p>}
      {konten.length === 0 && <p className="ch-muted">Instagram und Facebook sind nicht gebucht.</p>}
      <ul className="ch-karten">
        {konten.map((konto) => (
          <KontoKarte key={konto.platform} konto={konto} userName={userName} k={k} />
        ))}
      </ul>
    </div>
  );
}

function KontoKarte({ konto, userName, k }: { konto: HubSocialKonto; userName: string;
  k: ReturnType<typeof useSocialKonten> }) {
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const name = KANAL[konto.platform] ?? konto.platform;

  async function tun(arbeit: () => Promise<void>) {
    setBusy(true);
    setFehler(null);
    try {
      await arbeit();
    } catch (e) {
      setFehler(fehlerText(e).text);
    } finally {
      setBusy(false);
    }
  }

  const zurueck = () => window.location.href.split("#")[0] ?? window.location.href;
  const hinweis = ablauf(konto.token_expires_at);

  return (
    <li className="ch-karte">
      <div className="ch-kopf">
        <strong>{name}</strong>
        <span className={konto.verbunden ? "ch-plakette ch-plakette-ok" : konto.status ? "ch-plakette ch-plakette-warn"
          : "ch-plakette"}>
          {konto.verbunden ? "verbunden" : konto.status === "expired" ? "abgelaufen"
            : konto.status === "choose_page" ? "Seite wählen" : "nicht verbunden"}
        </span>
      </div>

      {konto.verbunden && (
        <p className="ch-text">
          {konto.handle ? `@${konto.handle}` : konto.name}
          {hinweis && <span className="ch-muted ch-klein"> · {hinweis}</span>}
        </p>
      )}
      {konto.verbunden && konto.kommentare === false && (
        <p className="ch-muted ch-klein">Für Kommentare bitte einmal neu verbinden — die Berechtigung kam später dazu.</p>
      )}
      {konto.status === "expired" && (
        <p className="ch-hinweis">Die Verbindung ist abgelaufen — bis sie erneuert ist, wird nichts automatisch gepostet.</p>
      )}
      {konto.last_error && konto.status !== "expired" && <p className="ch-muted ch-klein">{konto.last_error}</p>}

      {konto.status === "choose_page" && (
        <div className="ch-aktionen">
          {konto.pages.map((s) => (
            <button key={s.id} type="button" className="ch-knopf" disabled={busy}
              onClick={() => void tun(() => k.seite(s.id))}>{s.name}</button>
          ))}
        </div>
      )}

      {konto.verbunden && (
        <label className="ch-klein" style={{ display: "flex", gap: 8, alignItems: "center", margin: "6px 0" }}>
          <input type="checkbox" checked={konto.auto_post} disabled={busy}
            onChange={(e) => void tun(() => k.autoPost(konto.platform, e.target.checked))} />
          Freigegebene Beiträge automatisch zu ihrem Termin posten
        </label>
      )}

      <div className="ch-aktionen">
        {!konto.konfiguriert ? (
          <span className="ch-muted ch-klein">Die Verbindung zu {name} ist bei uns noch nicht eingerichtet.</span>
        ) : (
          <button type="button" className={konto.verbunden ? "ch-knopf" : "ch-knopf ch-knopf-primaer"} disabled={busy}
            onClick={() => void tun(() => k.verbinden(konto.platform, zurueck(), userName))}>
            {konto.verbunden ? "Neu verbinden" : konto.status === "expired" ? "Erneuern" : `${name} verbinden`}
          </button>
        )}
        {konto.status && (
          <button type="button" className="ch-knopf" disabled={busy}
            onClick={() => window.confirm(`${name} trennen? Danach wird dort nichts mehr automatisch gepostet.`)
              && void tun(() => k.trennen(konto.platform))}>
            Trennen
          </button>
        )}
      </div>
      {konto.platform === "instagram" && !konto.verbunden && konto.konfiguriert && (
        <p className="ch-muted ch-klein">Nur Business- oder Creator-Konten lassen sich verbinden (Instagram → Einstellungen → Kontotyp).</p>
      )}
      {konto.platform === "tiktok" && konto.konfiguriert && (
        <p className="ch-muted ch-klein">Nur Videos. Solange TikTok die App nicht geprüft hat, erscheinen Beiträge nur privat („Nur ich“).</p>
      )}
      {konto.platform === "facebook" && !konto.verbunden && konto.konfiguriert && (
        <p className="ch-muted ch-klein">Gepostet wird auf eine Facebook-Seite, die Sie verwalten — nicht ins private Profil.</p>
      )}
      {fehler && <p className="ch-hinweis">{fehler}</p>}
    </li>
  );
}
